// redux/slices/servicePackagesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getJwtToken } from "../../Utils/authStorage";
import { getServerUrl } from "../../Utils/serverSelector";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
  url,
  method = "GET",
  headers = {},
  data,
}) => {
  const jwt = await getJwtToken();

  const config = {
    url: `${getServerUrl()}${url}`,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      ...headers,
    },
  };

  if (data !== undefined) {
    config.data = data;
  }

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    throw {
      status: error?.response?.status,
      data: error?.response?.data,
      message:
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Request failed",
    };
  }
};

const getErrorMessage = (error, fallback) =>
  error?.data?.message ||
  error?.data?.error ||
  error?.message ||
  fallback;

/* ===================== THUNKS ===================== */

export const fetchServicePackages = createAsyncThunk(
  "servicePackages/fetchServicePackages",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const data = await axiosRequest({
        url: `/service-packages/${uid}`,
        method: "GET",
      });

      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Service Packages failed")
      );
    }
  }
);

export const fetchServicePackageById = createAsyncThunk(
  "servicePackages/fetchServicePackageById",
  async (id, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/service-packages/detail/${id}`,
        method: "GET",
      });

      return data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Fetch Service Package failed")
      );
    }
  }
);

export const createServicePackage = createAsyncThunk(
  "servicePackages/createServicePackage",
  async (packageData, { rejectWithValue }) => {
    try {
      if (!packageData?.uid) throw new Error("UID не передан");

      const data = await axiosRequest({
        url: "/service-packages",
        method: "POST",
        data: packageData,
      });

      return data?.servicePackage || data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Create Service Package failed")
      );
    }
  }
);

export const updateServicePackage = createAsyncThunk(
  "servicePackages/updateServicePackage",
  async ({ id, packageData }, { rejectWithValue }) => {
    try {

      if (!id) throw new Error("ID не передан");

      const data = await axiosRequest({
        url: `/service-packages/${id}`,
        method: "PUT",
        data: packageData || {},
      });


      return data?.servicePackage || data || null;
    } catch (error) {
      console.log("[SERVICE PACKAGE UPDATE ERROR]", {
        status: error?.status || error?.response?.status,
        data: error?.data || error?.response?.data,
        message: error?.message,
      });

      return rejectWithValue(
        getErrorMessage(error, "Update Service Package failed")
      );
    }
  }
);

export const deleteServicePackage = createAsyncThunk(
  "servicePackages/deleteServicePackage",
  async (arg, { rejectWithValue }) => {
    try {
      const id = typeof arg === "object" ? arg.id : arg;
      if (!id) throw new Error("ID не передан");

      await axiosRequest({
        url: `/service-packages/${id}`,
        method: "DELETE",
      });

      console.log("[SERVICE PACKAGE DELETE RESPONSE]", id);

      return id;
    } catch (error) {
      console.log("[SERVICE PACKAGE DELETE ERROR]", {
        status: error?.status || error?.response?.status,
        data: error?.data || error?.response?.data,
        message: error?.message,
      });

      return rejectWithValue(
        getErrorMessage(error, "Delete Service Package failed")
      );
    }
  }
);

export const reorderServicePackages = createAsyncThunk(
  "servicePackages/reorderServicePackages",
  async ({ uid, order }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const data = await axiosRequest({
        url: `/service-packages/${encodeURIComponent(uid)}/reorder`,
        method: "POST",
        data: { order },
      });

      return data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Reorder Service Packages failed")
      );
    }
  }
);

/* ===================== STATE ===================== */

const initialState = {
  servicePackages: [],
  currentPackage: null,
  status: "idle",
  error: null,
};

/* ===================== SLICE ===================== */

const servicePackagesSlice = createSlice({
  name: "servicePackages",
  initialState,
  reducers: {
    clearCurrentPackage: (state) => {
      state.currentPackage = null;
    },
    clearServicePackagesError: (state) => {
      state.error = null;
    },
    resetServicePackagesStatus: (state) => {
      state.status = "idle";
      state.error = null;
    },
    clearServicePackages: (state) => {
      state.servicePackages = [];
      state.currentPackage = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServicePackages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchServicePackages.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.servicePackages = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchServicePackages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Service Packages failed";
      })

      .addCase(fetchServicePackageById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchServicePackageById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentPackage = action.payload || null;
      })
      .addCase(fetchServicePackageById.rejected, (state, action) => {
        state.status = "failed";
        state.currentPackage = null;
        state.error =
          action.payload ||
          action.error?.message ||
          "Fetch Service Package failed";
      })

      .addCase(createServicePackage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createServicePackage.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) {
          state.servicePackages.push(action.payload);
        }
      })
      .addCase(createServicePackage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Create Service Package failed";
      })

      .addCase(updateServicePackage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateServicePackage.fulfilled, (state, action) => {
        state.status = "succeeded";

        const updatedPackage = action.payload;
        if (!updatedPackage?.id) return;

        const index = state.servicePackages.findIndex(
          (item) => String(item.id) === String(updatedPackage.id)
        );

        if (index !== -1) {
          state.servicePackages[index] = updatedPackage;
        }
      })
      .addCase(updateServicePackage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Update Service Package failed";
      })

      .addCase(deleteServicePackage.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteServicePackage.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.servicePackages = state.servicePackages.filter(
          (item) => String(item.id) !== String(action.payload)
        );
      })
      .addCase(deleteServicePackage.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Delete Service Package failed";
      })

      .addCase(reorderServicePackages.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(reorderServicePackages.fulfilled, (state, action) => {
        state.status = "succeeded";

        const list = action.payload?.packages || action.payload;

        if (Array.isArray(list)) {
          state.servicePackages = list;
        } else {
          state.servicePackages = [...state.servicePackages].sort((a, b) => {
            const ao = a.sortOrder ?? a.id ?? 0;
            const bo = b.sortOrder ?? b.id ?? 0;
            return ao - bo;
          });
        }
      })
      .addCase(reorderServicePackages.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Reorder Service Packages failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ===================== ACTIONS ===================== */

export const {
  clearCurrentPackage,
  clearServicePackagesError,
  resetServicePackagesStatus,
  clearServicePackages,
} = servicePackagesSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectServicePackagesState = (state) => state.servicePackages;
export const selectServicePackages = (state) =>
  state.servicePackages?.servicePackages || [];
export const selectCurrentServicePackage = (state) =>
  state.servicePackages?.currentPackage || null;
export const selectServicePackagesStatus = (state) =>
  state.servicePackages?.status || "idle";
export const selectServicePackagesError = (state) =>
  state.servicePackages?.error || null;

export default servicePackagesSlice.reducer;